<template>
  <div class="grouped-wrap list-shell">
    <div class="grouped-header list-header">
      <div class="grouped-header__left list-header__left">
        <div class="grouped-title">
          <i class="fa-duotone fa-layer-group"></i>
          <span>Grouped by Loan</span>
        </div>

        <div class="grouped-sub">
          <span class="chip chip--count">{{ totalConditions }} conditions</span>
          <span class="chip chip--count">{{ totalLoans }} loans</span>

          <button
            class="chip chip--count chip--filter"
            :class="{ 'chip--active': activeBucket === 'resub' }"
            type="button"
            @click="toggleBucket('resub')"
            :disabled="resubLoanCount === 0 || loading"
          >
            <i class="fa-duotone fa-arrow-rotate-left"></i>
            <span>{{ resubLoanCount }} resubmittals</span>
          </button>

          <button
            class="chip chip--count chip--filter"
            :class="{ 'chip--active': activeBucket === 'sub' }"
            type="button"
            @click="toggleBucket('sub')"
            :disabled="subLoanCount === 0 || loading"
          >
            <i class="fa-duotone fa-paper-plane"></i>
            <span>{{ subLoanCount }} submittals</span>
          </button>

          <button
            class="chip chip--count chip--filter"
            :class="{ 'chip--active': activeBucket === 'condl' }"
            type="button"
            @click="toggleBucket('condl')"
            :disabled="condlLoanCount === 0 || loading"
          >
            <i class="fa-duotone fa-circle-check"></i>
            <span>{{ condlLoanCount }} cond&apos;l approvals</span>
          </button>

          <button
            v-if="activeBucket !== 'all'"
            class="chip chip--count chip--clear"
            type="button"
            @click="clearBucket"
            :disabled="loading"
          >
            <i class="fa-duotone fa-xmark"></i>
            <span>Clear</span>
          </button>
        </div>
      </div>

      <div class="grouped-header__right list-header__right">
        <div class="searchbox">
          <i class="fa-duotone fa-magnifying-glass searchbox__icon"></i>

          <input
            class="searchbox__input"
            type="text"
            :value="searchText"
            placeholder="Search borrower, loan #, condition, address, status, LO/Proc/UW…"
            @input="$emit('search-input', $event.target.value)"
            :disabled="loading"
          />

          <button
            v-if="searchText"
            class="searchbox__clear"
            type="button"
            title="Clear"
            @click="$emit('search-clear')"
            :disabled="loading"
          >
            <i class="fa-duotone fa-xmark"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="grouped">
      <div v-if="loading" class="grouped-loading">
        <div class="grouped-loading__card">
          <div class="spinner" />
          <div class="grouped-loading__text">Loading loans…</div>
        </div>
      </div>

      <template v-else>
        <div v-for="g in pagedGroupedLoans" :key="g.loanNumber" class="loan-card">
          <div class="loan-card__header">
            <div class="loan-left">
              <div class="loan-top">
                <div class="loan-title">
                  <i class="fa-duotone fa-folder-tree"></i>
                  <span>Loan # {{ g.loanNumber || "—" }}</span>
                  <span class="loan-count">{{ g.items.length }} conditions</span>

                  <span class="milestone-pill" :class="milestoneClass(g.rank.kind)">
                    <i class="fa-duotone" :class="milestoneIcon(g.rank.kind)"></i>
                    <span>{{ milestoneLabel(g.rank.kind) }}</span>
                    <span v-if="g.rank.dateStr">• {{ g.rank.dateStr }}</span>
                  </span>
                </div>

                <div class="loan-amount" v-if="g.head.loanAmount">
                  {{ formatCurrency(g.head.loanAmount) }}
                </div>
              </div>

              <div class="loan-sub">
                <span class="loan-borrower clamp-1" :title="g.head.borrowerName || '—'">
                  {{ g.head.borrowerName || "—" }}
                </span>
                <span class="dot">•</span>
                <span class="loan-address clamp-1" :title="g.head.fullPropertyAddress || '—'">
                  {{ g.head.fullPropertyAddress || "—" }}
                </span>
              </div>

              <div class="loan-meta">
                <div class="loan-status">
                  <div class="badge" :class="statusClass(g.head)" :title="g.head.msstatus">
                    {{ g.head.msstatus || "—" }}
                  </div>
                  <div class="status-date">
                    <i class="fa-duotone fa-calendar-days"></i>
                    <span>{{ g.head.estimatedClosingDate || "—" }}</span>
                  </div>
                </div>

                <div class="loan-team">
                  <TeamCompactCell
                    :item="g.head"
                    :photo-url="photoUrl"
                    :is-photo-loading="isPhotoLoading"
                    @open-teams="m => $emit('open-teams', m)"
                    @open-email="(m, item) => $emit('open-email', m, item)"
                    @img-error="id => $emit('img-error', id)"
                  />
                </div>
              </div>
            </div>

            <div class="loan-actions">
              <button
                v-if="isUwOrSupport"
                class="action-pill"
                @click="openUwSequence(g.head, null)"
                title="Open (Encompass + Swift)"
              >
                <i class="fa-duotone fa-arrow-up-right-from-square"></i>
                <span>Open</span>
              </button>

              <template v-else>
                <button
                  class="action-pill"
                  @click="openLoanAndTrack(g.head, null)"
                  title="Open in Swift"
                >
                  <i class="fa-duotone fa-arrow-up-right-from-square"></i>
                  <span>Swift</span>
                </button>

                <button
                  class="action-pill"
                  @click="openEncompassAndTrack(g.head)"
                  title="Open Encompass"
                >
                  <i class="fa-duotone fa-arrow-up-right-from-square"></i>
                  <span>Encompass</span>
                </button>
              </template>
            </div>
          </div>

          <div class="loan-card__body">
            <div v-for="c in g.items" :key="c.conditionId || c.id || c.title" class="cond-row">
              <div class="cond-left">
                <div class="cond-title clamp-1" :title="c.title">
                  {{ c.title || "—" }}
                </div>

                <div class="cond-reason clamp-2" :title="c.attentionReason">
                  {{ c.attentionReason || "—" }}
                </div>
              </div>

              <div class="cond-right">
                <div class="cond-time">
                  <i class="fa-duotone fa-clock"></i>
                  <span>{{ c.attentionSetAt || "—" }}</span>
                </div>

                <button
                  class="cond-action"
                  @click="isUwOrSupport ? openUwSequence(g.head, c.conditionId) : openLoanAndTrack(g.head, c.conditionId)"
                  title="Open Condition"
                >
                  <i class="fa-duotone fa-arrow-up-right-from-square"></i>
                  <span>Open</span>
                </button>

                <button
                  class="cond-action"
                  @click="$emit('open-video', c.conditionId)"
                  title="Watch Video"
                >
                  <i class="fa-duotone fa-video"></i>
                  <span>Video</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!groupedLoans.length" class="empty">
          No items
        </div>
      </template>
    </div>

    <div class="list-pager" v-if="!loading && groupedLoans.length && totalLoanPages > 1">
      <button class="pager-btn" :disabled="loanSkip === 0" @click="prevLoanPage">
        <i class="fa-duotone fa-chevron-left"></i>
        Prev
      </button>

      <div class="pager-meta">
        Loan Page {{ loanPage }} / {{ totalLoanPages }}
      </div>

      <button class="pager-btn" :disabled="loanSkip + pageSize >= totalLoans" @click="nextLoanPage">
        Next
        <i class="fa-duotone fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
import TeamCompactCell from "./TeamCompactCell.vue"

const LS_RECENT_LOANS = "initialuw_trends_recent_loans_v1"

export default {
  name: "ConditionsLoanGrouped",
  components: { TeamCompactCell },
  props: {
    items: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    pageSize: { type: Number, default: 10 },
    loading: { type: Boolean, default: false },
    photoUrl: { type: Function, required: true },
    isPhotoLoading: { type: Function, required: true },
    closePillClassFn: { type: Function, default: null },
    searchText: { type: String, default: "" }
  },
  data() {
    return {
      loanSkip: 0,
      activeBucket: "all",
      recentLoans: [],
        userRoles: [],
      activeUserRoles: [],
    }
  },
  computed: {
 isUwOrSupport() {
  const roles = Array.isArray(this.activeUserRoles)
    ? this.activeUserRoles
    : String(this.activeUserRoles || "").split(",")

  return roles
    .map(x => String(x || "").trim().toLowerCase())
    .some(r => r === "underwriter" || r === "uw support")
},
    safeItems() {
      return Array.isArray(this.items) ? this.items : []
    },
    filteredItems() {
      const q = String(this.searchText || "").trim().toLowerCase()
      if (!q) return this.safeItems

      const fields = [
        "loanNumber",
        "borrowerName",
        "title",
        "attentionReason",
        "fullPropertyAddress",
        "msstatus",
        "orgId",
        "loname",
        "procname",
        "uwname",
        "attentionSetAt",
        "estimatedClosingDate"
      ]

      return this.safeItems.filter(it => {
        for (const f of fields) {
          const v = String(it?.[f] || "").toLowerCase()
          if (v.includes(q)) return true
        }
        return false
      })
    },
    groupedLoansAll() {
      const map = new Map()
      for (const it of this.filteredItems) {
        const loan = String(it?.loanNumber || "—")
        if (!map.has(loan)) map.set(loan, [])
        map.get(loan).push(it)
      }

      return Array.from(map.entries())
        .map(([loanNumber, items]) => {
          const head = items[0] || {}
          const rank = this.loanRank(items)
          return { loanNumber, head, items, rank }
        })
        .filter(g => !g.rank.isApproved)
    },
    groupedLoans() {
      let groups = this.groupedLoansAll

      if (this.activeBucket !== "all") {
        groups = groups.filter(g => g.rank.bucket === this.activeBucket)
      }

      groups.sort((a, b) => {
        if (a.rank.kind !== b.rank.kind) return a.rank.kind - b.rank.kind
        if (a.rank.date !== b.rank.date) return a.rank.date - b.rank.date
        return String(a.loanNumber).localeCompare(String(b.loanNumber), undefined, { numeric: true, sensitivity: "base" })
      })

      return groups
    },
    totalLoans() {
      return this.groupedLoans.length
    },
    totalConditions() {
      return this.groupedLoans.reduce((sum, g) => sum + (g.items?.length || 0), 0)
    },
    resubLoanCount() {
      return this.groupedLoansAll.filter(g => g.rank.bucket === "resub").length
    },
    subLoanCount() {
      return this.groupedLoansAll.filter(g => g.rank.bucket === "sub").length
    },
    condlLoanCount() {
      return this.groupedLoansAll.filter(g => g.rank.bucket === "condl").length
    },
    totalLoanPages() {
      return Math.max(1, Math.ceil(this.totalLoans / this.pageSize))
    },
    loanPage() {
      return Math.floor((this.loanSkip || 0) / (this.pageSize || 1)) + 1
    },
    pagedGroupedLoans() {
      return this.groupedLoans.slice(this.loanSkip, this.loanSkip + this.pageSize)
    }
  },
  watch: {
    searchText() {
      this.loanSkip = 0
      this.activeBucket = "all"
      this.applyModeDefaultsIfNeeded()
    },
    items() {
      this.loanSkip = 0
      this.activeBucket = "all"
      this.applyModeDefaultsIfNeeded()
    },
    "$route.query.mode"() {
      this.applyModeDefaultsIfNeeded()
    }
  },
  mounted() {
      this.userRoles = this.getRolesFromStorage("user_roles")
      this.activeUserRoles = this.getRolesFromStorage("active_user_roles")
    this.applyModeDefaultsIfNeeded()
    this.loadRecentLoans()
  },
  methods: {
        getRolesFromStorage(key) {
      const raw = localStorage.getItem(key)
      if (!raw) return []
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed.map(x => String(x || "").trim()).filter(Boolean)
        return String(parsed || "")
          .split(",")
          .map(x => String(x || "").trim())
          .filter(Boolean)
      } catch {
        return String(raw || "")
          .split(",")
          .map(x => String(x || "").trim())
          .filter(Boolean)
      }
    },
    loadRecentLoans() {
      try {
        const raw = localStorage.getItem(LS_RECENT_LOANS)
        if (!raw) {
          this.recentLoans = []
          return
        }
        const parsed = JSON.parse(raw)
        this.recentLoans = Array.isArray(parsed) ? parsed.slice(0, 10) : []
      } catch {
        this.recentLoans = []
      }
    },

    writeRecentLoan(head) {
      const loanId = head?.loanId
      if (!loanId) return

      const borrowerName = head?.borrowerName || ""
      const cityState = `${head?.propertyCity || ""}, ${head?.propertyState || ""}`.replace(/^,\s*|,\s*$/g, "")

      const nextItem = {
        loanId,
        loanNumber: head?.loanNumber || "",
        borrowerName,
        loanAmount: head?.loanAmount || null,
        cityState,
        openedAt: new Date().toISOString()
      }

      const existing = Array.isArray(this.recentLoans) ? this.recentLoans : []
      const filtered = existing.filter(x => x.loanId !== loanId)
      const next = [nextItem, ...filtered].slice(0, 10)

      this.recentLoans = next
      try {
        localStorage.setItem(LS_RECENT_LOANS, JSON.stringify(next))
      } catch {}
    },

    openLoanAndTrack(head, conditionId = null) {
      this.writeRecentLoan(head)
      this.$emit("open-loan", head.loanId, conditionId, head)
    },

    openEncompassAndTrack(head) {
      this.writeRecentLoan(head)
      this.$emit("open-encompass", head.loanId, head)
    },

    openUwSequence(head, conditionId) {
      this.writeRecentLoan(head)
      this.$emit("open-encompass", head.loanId, head)
   
      this.$emit("open-loan", head.loanId, null, head)

         if (conditionId) 
         {this.$emit("open-loan", head.loanId, conditionId, head)}
    },

    getQueryMode() {
      const q = this.$route?.query || {}
      return String(q.mode || "").toLowerCase().trim()
    },
    applyModeDefaultsIfNeeded() {
      const mode = this.getQueryMode()
      if (mode !== "newunderwrites") return
      if (this.subLoanCount === 0) return
      this.activeBucket = "sub"
      this.loanSkip = 0
    },
    toggleBucket(b) {
      this.activeBucket = this.activeBucket === b ? "all" : b
      this.loanSkip = 0
    },
    clearBucket() {
      this.activeBucket = "all"
      this.loanSkip = 0
    },

    isDefaultDate(v) {
      const s = String(v || "").trim()
      return !s || s.startsWith("1900-01-01") || s.startsWith("0001-01-01")
    },
    hasRealDate(v) {
      return !this.isDefaultDate(v)
    },
    isValidDate(v) {
      const s = String(v || "").trim()
      if (!s) return false
      if (s.startsWith("0001-01-01")) return false
      if (s.startsWith("1900-01-01")) return false
      const t = Date.parse(s)
      return Number.isFinite(t) && t > 0
    },
    dateTs(v) {
      if (!this.isValidDate(v)) return null
      const t = Date.parse(String(v))
      return Number.isFinite(t) ? t : null
    },
    fmtDate(v) {
      const t = this.dateTs(v)
      if (t == null) return ""
      return new Date(t).toLocaleDateString("en-US")
    },

    isSubmittalNewUw(head) {
      return (
        this.hasRealDate(head?.submittalDate) &&
        this.isDefaultDate(head?.condlAppDate) &&
        this.isDefaultDate(head?.initialCondAppDate) &&
        this.isDefaultDate(head?.approvalDate) &&
        this.isDefaultDate(head?.resubmittalDate)
      )
    },

    loanRank(items) {
      const head = items?.[0] || {}
      const approved = this.hasRealDate(head?.approvalDate)

      if (approved) {
        return { isApproved: true, bucket: "approved", kind: 99, date: Number.POSITIVE_INFINITY, dateStr: "" }
      }

      const list = []

      const resubTs = this.dateTs(head?.resubmittalDate)
      if (resubTs != null) {
        list.push({ bucket: "resub", kind: 0, ts: resubTs, str: this.fmtDate(head?.resubmittalDate) })
      }

      if (this.isSubmittalNewUw(head)) {
        const subTs = this.dateTs(head?.submittalDate)
        if (subTs != null) {
          list.push({ bucket: "sub", kind: 1, ts: subTs, str: this.fmtDate(head?.submittalDate) })
        }
      }

      const condlTs = this.dateTs(head?.condlAppDate)
      if (condlTs != null) {
        list.push({ bucket: "condl", kind: 2, ts: condlTs, str: this.fmtDate(head?.condlAppDate) })
      }

      if (!list.length) {
        return { isApproved: false, bucket: "none", kind: 3, date: Number.POSITIVE_INFINITY, dateStr: "" }
      }

      list.sort((a, b) => b.ts - a.ts)
      const active = list[0]

      return { isApproved: false, bucket: active.bucket, kind: active.kind, date: active.ts, dateStr: active.str }
    },

    milestoneLabel(kind) {
      if (kind === 0) return "RESUB"
      if (kind === 1) return "SUBMITTED"
      if (kind === 2) return "COND’L"
      return "PIPELINE"
    },
    milestoneIcon(kind) {
      if (kind === 0) return "fa-arrow-rotate-left"
      if (kind === 1) return "fa-paper-plane"
      if (kind === 2) return "fa-circle-check"
      return "fa-wave-pulse"
    },
    milestoneClass(kind) {
      if (kind === 0) return "milestone--resub"
      if (kind === 1) return "milestone--sub"
      if (kind === 2) return "milestone--condl"
      return "milestone--neutral"
    },
    prevLoanPage() {
      this.loanSkip = Math.max(0, this.loanSkip - this.pageSize)
    },
    nextLoanPage() {
      if (this.loanSkip + this.pageSize < this.totalLoans) {
        this.loanSkip += this.pageSize
      }
    },
    statusClass(item) {
      const s = String(item?.msstatus || "").toLowerCase()
      if (!s) return "badge--neutral"
      if (s.includes("clear") || s.includes("approved") || s.includes("complete")) return "badge--good"
      if (s.includes("suspend") || s.includes("declin") || s.includes("denied")) return "badge--bad"
      if (s.includes("review") || s.includes("pending") || s.includes("submitted")) return "badge--warn"
      return "badge--neutral"
    },
    formatCurrency(v) {
      const n = Number(v)
      if (isNaN(n)) return "—"
      return n.toLocaleString("en-US", { style: "currency", currency: "USD" })
    }
  }
}
</script>

<style scoped>
.grouped-wrap {
  height: 100%;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.grouped-loading {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 26px 12px;
}

.grouped-loading__card {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(40, 110, 255, 0.22);
  background: rgba(40, 110, 255, 0.08);
  box-shadow: 0 10px 24px rgba(40, 110, 255, 0.12);
}

.grouped-loading__text {
  font-size: 12px;
  font-weight: 950;
  color: var(--text-primary-on-background);
}

.spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(40, 110, 255, 0.22);
  border-top-color: rgba(40, 110, 255, 0.95);
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style scoped>
/* (rest of your existing styles unchanged) */
.grouped-wrap {
  height: 100%;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.grouped-header {
  display: grid;
  grid-template-columns: auto minmax(280px, 1fr);
  align-items: center;
  gap: 12px;
  padding: 10px 10px;
  border-bottom: 1px solid var(--border-ltr);
  background: rgba(5, 29, 58, 0.03);
}

.grouped-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  font-weight: 1000;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-secondary-on-background);
}

.grouped-title i {
  color: var(--notification-blue);
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(40, 110, 255, 0.45);
  font-size: 13px;
}

.grouped-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(5, 29, 58, 0.12);
  background: rgba(5, 29, 58, 0.04);
  font-size: 11px;
  font-weight: 950;
  color: var(--text-secondary-on-background);
  line-height: 1;
}

.chip--count {
  border-color: rgba(40, 110, 255, 0.18);
  background: rgba(40, 110, 255, 0.06);
  color: var(--text-primary-on-background);
}

.searchbox {
  position: relative;
  width: 100%;
}

.searchbox__icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.75;
  pointer-events: none;
}

.searchbox__input {
  width: 100%;
  height: 40px;
  padding: 0 40px 0 38px;
  border: 1px solid var(--border-lt);
  border-radius: 12px;
  background: var(--background);
  color: var(--text-primary-on-background);
  font-size: 13px;
  font-weight: 800;
}

.searchbox__input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 2px rgba(5, 29, 58, 0.15);
}

.searchbox__clear {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: 0.85;
}

.searchbox__clear:hover {
  opacity: 1;
  color: var(--primary);
}

.grouped {
  height: 100%;
  width: 100%;
  overflow: auto;
  padding: 10px 8px 12px 8px;
  background: var(--background);
}

.loan-card {
  border: 1px solid rgba(5, 29, 58, 0.1);
  background: rgba(5, 29, 58, 0.03);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 10px;
}

.loan-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 12px;
  background: rgba(40, 110, 255, 0.05);
  border-bottom: 1px solid rgba(5, 29, 58, 0.08);
}

.loan-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.loan-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.loan-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 1000;
  font-size: 13px;
  color: var(--text-primary-on-background);
}

.loan-title i {
  color: var(--notification-blue);
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(40, 110, 255, 0.45);
}

.loan-count {
  font-size: 11px;
  font-weight: 1000;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(5, 29, 58, 0.14);
  background: rgba(5, 29, 58, 0.04);
  color: var(--text-secondary-on-background);
  line-height: 1;
}

.loan-amount {
  font-size: 13px;
  font-weight: 1000;
  color: var(--primary);
  white-space: nowrap;
}

.loan-sub {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 850;
  color: var(--text-secondary-on-background);
  min-width: 0;
}

.dot {
  opacity: 0.5;
}

.loan-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.loan-status {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.badge {
  padding: 6px 10px;
  border-radius: 12px;
  border: 1px solid rgba(5, 29, 58, 0.15);
  font-size: 11px;
  font-weight: 1000;
  background: rgba(5, 29, 58, 0.06);
  color: var(--text-primary-on-background);
}

.badge--good {
  background: rgba(0, 180, 60, 0.12);
  border-color: rgba(0, 180, 60, 0.25);
}

.badge--warn {
  background: rgba(255, 200, 0, 0.15);
  border-color: rgba(255, 200, 0, 0.3);
}

.badge--bad {
  background: rgba(255, 0, 0, 0.12);
  border-color: rgba(255, 0, 0, 0.25);
}

.badge--neutral {
  background: rgba(5, 29, 58, 0.06);
  border-color: rgba(5, 29, 58, 0.15);
}

.status-date {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 850;
  color: var(--text-secondary-on-background);
  padding-left: 2px;
  opacity: 0.95;
}

.loan-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid rgba(5, 29, 58, 0.12);
  background: rgba(5, 29, 58, 0.04);
  cursor: pointer;
  user-select: none;
  transition: transform 0.12s ease, background 0.12s ease, border-color 0.12s ease;
}

.action-pill span {
  font-size: 11px;
  font-weight: 1000;
  color: var(--text-primary-on-background);
  white-space: nowrap;
  line-height: 1;
}

.action-pill i {
  font-size: 13px;
  color: var(--notification-blue);
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(40, 110, 255, 0.45);
}

.action-pill:hover {
  background: rgba(40, 110, 255, 0.08);
  border-color: rgba(40, 110, 255, 0.22);
  transform: translateY(-1px);
}

.loan-card__body {
  background: var(--background);
}

.cond-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}

.cond-row:hover {
  background: rgba(5, 29, 58, 0.03);
  box-shadow: inset 3px 0 0 var(--notification-blue);
}

.cond-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.cond-title {
  font-weight: 1000;
  font-size: 13px;
  color: var(--text-primary-on-background);
}

.cond-reason {
  font-weight: 750;
  font-size: 12px;
  color: var(--text-secondary-on-background);
}

.cond-right {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.cond-time {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 850;
  color: var(--text-secondary-on-background);
}

.cond-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 12px;
  border: 1px solid rgba(5, 29, 58, 0.12);
  background: rgba(5, 29, 58, 0.04);
  cursor: pointer;
  user-select: none;
  transition: transform 0.12s ease, background 0.12s ease, border-color 0.12s ease;
}

.cond-action span {
  font-size: 11px;
  font-weight: 1000;
  color: var(--text-primary-on-background);
  white-space: nowrap;
  line-height: 1;
}

.cond-action i {
  font-size: 13px;
  color: var(--notification-blue);
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(40, 110, 255, 0.45);
}

.cond-action:hover {
  background: rgba(40, 110, 255, 0.08);
  border-color: rgba(40, 110, 255, 0.22);
  transform: translateY(-1px);
}

.clamp-1 {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty {
  padding: 18px 10px;
  color: var(--text-secondary-on-background);
  font-weight: 900;
}

.list-pager {
  padding: 10px 12px;
  border-top: 1px solid var(--border-ltr);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: var(--background);
}

.pager-btn {
  border: 1px solid var(--border-lt);
  background: var(--button);
  color: var(--on-button);
  padding: 7px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.pager-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.pager-meta {
  font-size: 12px;
  color: var(--text-secondary-on-background);
  font-weight: 800;
}

.grouped-wrap :deep(i.fa-duotone),
.grouped-wrap :deep(i.fa-solid),
.grouped-wrap :deep(i.fa-regular),
.grouped-wrap :deep(i.fa-brands) {
  color: var(--notification-blue);
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(40, 110, 255, 0.45);
}

.grouped-wrap :deep(.chip i),
.grouped-wrap :deep(.status-date i),
.grouped-wrap :deep(.cond-time i),
.grouped-wrap :deep(.loan-sub i),
.grouped-wrap :deep(.loan-meta i) {
  color: rgba(40, 110, 255, 0.7);
  --fa-primary-color: rgba(40, 110, 255, 0.7);
  --fa-secondary-color: rgba(40, 110, 255, 0.35);
}

.chip--filter {
  cursor: pointer;
  user-select: none;
  transition: transform 0.12s ease, background 0.12s ease, border-color 0.12s ease;
}

.chip--filter:hover:not(:disabled) {
  background: rgba(40, 110, 255, 0.1);
  border-color: rgba(40, 110, 255, 0.28);
  transform: translateY(-1px);
}

.chip--filter:disabled {
  opacity: 0.55;
  cursor: default;
}

.chip--active {
  background: rgba(40, 110, 255, 0.18);
  border-color: rgba(40, 110, 255, 0.35);
  box-shadow: inset 0 0 0 1px rgba(40, 110, 255, 0.22);
}

.chip--clear {
  border-color: rgba(255, 0, 0, 0.2);
  background: rgba(255, 0, 0, 0.08);
}

.chip--clear:hover {
  background: rgba(255, 0, 0, 0.12);
  border-color: rgba(255, 0, 0, 0.25);
}

.milestone-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid rgba(5, 29, 58, 0.14);
  background: rgba(5, 29, 58, 0.05);
  font-size: 11px;
  font-weight: 1000;
  color: var(--text-secondary-on-background);
  line-height: 1;
  white-space: nowrap;
}

.milestone-pill i {
  font-size: 12px;
}

.milestone--resub {
  background: rgba(255, 120, 0, 0.14);
  border-color: rgba(255, 120, 0, 0.28);
  color: var(--text-primary-on-background);
}

.milestone--sub {
  background: rgba(40, 110, 255, 0.14);
  border-color: rgba(40, 110, 255, 0.28);
  color: var(--text-primary-on-background);
}

.milestone--condl {
  background: rgba(0, 180, 60, 0.14);
  border-color: rgba(0, 180, 60, 0.28);
  color: var(--text-primary-on-background);
}

.milestone--neutral {
  background: rgba(5, 29, 58, 0.06);
  border-color: rgba(5, 29, 58, 0.14);
}
</style>
